import React, { useEffect, useState } from "react";
import { Pagination } from "@heroui/pagination";

const EarningsData = ({ earnings }) => {
  const itemsPerPage = 2;
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Filter and sort earnings by the past year and organize by quarters
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today's date

  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const getQuarter = (date) => {
    const month = date.getMonth();
    if (month < 3) return 1;
    if (month < 6) return 2;
    if (month < 9) return 3;
    return 4;
  };

  const filteredAndSortedEarnings = earnings
    .filter((earning) => {
      const earningDate = new Date(earning.date);
      return earningDate >= oneYearAgo && earningDate <= today;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date),
        dateB = new Date(b.date);
      const quarterA = getQuarter(dateA),
        quarterB = getQuarter(dateB);
      if (dateA.getFullYear() !== dateB.getFullYear()) {
        return dateB.getFullYear() - dateA.getFullYear();
      }
      return quarterB - quarterA;
    });

  // Calculate the total number of pages
  const pageCount = Math.ceil(filteredAndSortedEarnings.length / itemsPerPage);

  // Calculate the index of the last and first item of the current page
  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;

  // Slice the data to get only the items for the current page
  const currentEarningsData = filteredAndSortedEarnings.slice(
    firstIndex,
    lastIndex
  );

  // Change page
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    setIsLoading(false);
  }, [currentEarningsData]);

  const formatNumber = (num) => {
    if (num >= 1e12) {
      return (num / 1e12).toFixed(2) + " T";
    } else if (num >= 1e9) {
      return (num / 1e9).toFixed(2) + " B";
    } else if (num >= 1e6) {
      return (num / 1e6).toFixed(2) + " M";
    } else if (num === 0) {
      return "N/A";
    } else {
      return num.toLocaleString();
    }
  };

  if (currentEarningsData.length === 0) {
    return <div>Not available for this company.</div>;
  }
  const getValueColor = (estimated, actual) => {
    if (actual === null || actual === undefined) return ""; // No color if actual value is not available
    if (estimated === null || estimated === undefined) return ""; // No color if estimated value is not available
    return actual > estimated
      ? "text-green-600 text-shadow font-semibold"
      : "text-red-500";
  };

  return (
    <div className="bg-white text-sm  w-full">
      <div className="space-y-4">
        {currentEarningsData.map((item, index) => (
          <div key={index} className="p-4 bg-E6E6E6 border rounded-lg">
            <div className="grid grid-cols-3">
              <span className="font-bold mb-1">Date: {item.date}</span>
              <span className="font-bold text-center">Estimated</span>
              <span className="font-bold text-center">Actual</span>
            </div>
            <div className="grid grid-cols-3">
              <span className=" mb-1">EPS :</span>
              <span className="text-center ">{item.epsEstimated || "N/A"}</span>
              <span
                className={`${getValueColor(
                  item.epsEstimated,
                  item.eps
                )} text-center`}
              >
                {item.eps || "N/A"}
              </span>
            </div>
            <div className="grid grid-cols-3">
              <span className="  mb-1">Revenue:</span>
              <span className=" text-center ">
                $
                {item.revenueEstimated
                  ? formatNumber(Number(item.revenueEstimated))
                  : "N/A"}{" "}
              </span>
              <span
                className={`${getValueColor(
                  item.revenueEstimated,
                  item.revenue
                )} inline-block w-full text-center`}
              >
                ${item.revenue ? formatNumber(Number(item.revenue)) : "N/A"}
              </span>
            </div>
            <div className="grid grid-cols-3">
              <span className="">Time:</span>
              <span className="uppercase text-center">{item.time}</span>
            </div>
          </div>
        ))}
      </div>
      {pageCount > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            total={pageCount}
            initialPage={1}
            page={currentPage}
            onChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default EarningsData;
